import { useState } from "react";
import clsx from "clsx";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGetIndividualCapacityQuery } from "@/store/services/capacity/capacity.service";

type AllocationUnit = "fte_pct" | "fte_fraction";

export type BookingModalState =
    | {
          mode: "create";
          individualId: string;
          individualName: string;
          startDate: string; // YYYY-MM-DD
          endDate: string; // YYYY-MM-DD
      }
    | {
          mode: "edit";
          bookingId: string;
          individualId: string;
          individualName: string;
          startDate: string; // YYYY-MM-DD
          endDate: string; // YYYY-MM-DD
          projectId: string;
          serviceId: string;
          fte: number; // 0–1
          allocationUnit: AllocationUnit;
      };

type Service = {
    id: string;
    departmentId: string;
    committedManDays: number;
    bookedManDays: number;
};
type Budget = {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    manDaysPerManMonth: number;
    services: Service[];
};
type Project = { id: string; name: string; budgets: Budget[] };
type Department = { id: string; name: string };

type Props = {
    state: BookingModalState;
    projects: Project[];
    departments: Department[];
    onConfirm: (
        serviceId: string,
        fte: number,
        allocationUnit: AllocationUnit,
        startDate: string,
        endDate: string,
    ) => Promise<void>;
    onDelete?: () => Promise<void>;
    onClose: () => void;
};

function findInitialBudgetId(projects: Project[], projectId: string, serviceId: string): string {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return "";
    const budget = project.budgets.find((b) => b.services.some((c) => c.id === serviceId));
    return budget?.id ?? "";
}

function countMonths(startDate: string, endDate: string): number {
    const start = new Date(startDate + "T00:00:00Z");
    const end = new Date(endDate + "T00:00:00Z");
    return (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + (end.getUTCMonth() - start.getUTCMonth()) + 1;
}

export const BookingModal = ({ state, projects, departments, onConfirm, onDelete, onClose }: Props) => {
    const isEdit = state.mode === "edit";

    const [projectId, setProjectId] = useState<string>(isEdit ? state.projectId : "");
    const [budgetId, setBudgetId] = useState<string>(
        isEdit ? findInitialBudgetId(projects, state.projectId, state.serviceId) : "",
    );
    const [serviceId, setServiceId] = useState<string>(isEdit ? state.serviceId : "");
    const [allocationUnit, setAllocationUnit] = useState<AllocationUnit>(isEdit ? state.allocationUnit : "fte_pct");
    const [ftePct, setFtePct] = useState<string>(
        isEdit && state.allocationUnit === "fte_pct" ? String(Math.round(state.fte * 100)) : "100",
    );
    const [fteFraction, setFteFraction] = useState<string>(
        isEdit && state.allocationUnit === "fte_fraction" ? String(state.fte) : "1",
    );
    const [startDate, setStartDate] = useState<string>(state.startDate);
    const [endDate, setEndDate] = useState<string>(state.endDate);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { data: capacityData } = useGetIndividualCapacityQuery(
        {
            individualId: state.individualId,
            startDate,
            endDate,
            excludeBookingId: isEdit ? state.bookingId : undefined,
        },
        { skip: !startDate || !endDate || endDate < startDate },
    );

    // For edit mode: fetch capacity at the original booking dates to compute the prior contribution
    const { data: originalCapacityData } = useGetIndividualCapacityQuery(
        {
            individualId: state.individualId,
            startDate: isEdit ? state.startDate : startDate,
            endDate: isEdit ? state.endDate : endDate,
        },
        { skip: !isEdit },
    );

    const selectedProject = projects.find((p) => p.id === projectId);
    const budgets = selectedProject?.budgets ?? [];
    const selectedBudget = budgets.find((b) => b.id === budgetId);
    const services = selectedBudget?.services ?? [];
    const manDaysPerManMonth = selectedBudget?.manDaysPerManMonth ?? 20;

    const deptName = (deptId: string) => departments.find((d) => d.id === deptId)?.name ?? deptId;

    const selectedService = services.find((c) => c.id === serviceId);
    const committedMd = selectedService ? selectedService.committedManDays : null;
    const bookedMd = selectedService ? selectedService.bookedManDays : null;
    const remainingMd = committedMd !== null && bookedMd !== null ? committedMd - bookedMd : null;

    // Warning 1: booking dates fall outside the selected budget
    const dateOutsideBudget =
        selectedBudget !== undefined &&
        startDate &&
        endDate &&
        (startDate < selectedBudget.startDate || endDate > selectedBudget.endDate);

    // Compute new contribution MD based on allocation mode
    const ftePctNum = parseFloat(ftePct);
    const fteFractionNum = parseFloat(fteFraction);

    const newContributionMd = (() => {
        if (allocationUnit === "fte_pct") {
            return !isNaN(ftePctNum) && capacityData !== undefined
                ? (ftePctNum / 100) * capacityData.effectiveCapacityMd
                : null;
        } else {
            if (!isNaN(fteFractionNum) && startDate && endDate && endDate >= startDate) {
                return fteFractionNum * manDaysPerManMonth * countMonths(startDate, endDate);
            }
            return null;
        }
    })();

    const previousContributionMd = (() => {
        if (!isEdit || originalCapacityData === undefined) return 0;
        if (state.allocationUnit === "fte_pct") {
            return state.fte * originalCapacityData.effectiveCapacityMd;
        } else {
            return state.fte * manDaysPerManMonth * countMonths(state.startDate, state.endDate);
        }
    })();

    // Warning 2: would exceed service commitment
    const wouldExceedCommitment =
        committedMd !== null &&
        bookedMd !== null &&
        newContributionMd !== null &&
        bookedMd - previousContributionMd + newContributionMd > committedMd;

    // Warning 3: FTE fraction would overbook individual
    const wouldOverbookIndividual =
        allocationUnit === "fte_fraction" &&
        !isNaN(fteFractionNum) &&
        capacityData !== undefined &&
        capacityData.effectiveCapacityMd > 0 &&
        startDate &&
        endDate &&
        endDate >= startDate &&
        fteFractionNum * manDaysPerManMonth * countMonths(startDate, endDate) > capacityData.effectiveCapacityMd;

    const handleProjectChange = (val: string) => {
        setProjectId(val);
        setBudgetId("");
        setServiceId("");
    };

    const handleBudgetChange = (val: string) => {
        setBudgetId(val);
        setServiceId("");
    };

    const handleConfirm = async () => {
        if (!projectId) {
            setError("Please select a project.");
            return;
        }
        if (!budgetId) {
            setError("Please select a budget.");
            return;
        }
        if (!serviceId) {
            setError("Please select a service.");
            return;
        }
        if (!startDate || !endDate) {
            setError("Please select a valid period.");
            return;
        }
        if (endDate < startDate) {
            setError("End date must not be before start date.");
            return;
        }

        let fte: number;
        if (allocationUnit === "fte_pct") {
            const pct = parseFloat(ftePct);
            if (isNaN(pct) || pct < 1 || pct > 100) {
                setError("Allocation must be between 1 and 100%.");
                return;
            }
            fte = pct / 100;
        } else {
            const frac = parseFloat(fteFraction);
            if (isNaN(frac) || frac < 0.1 || frac > 1.0) {
                setError("Budget FTE must be between 0.1 and 1.0.");
                return;
            }
            fte = frac;
        }

        setSaving(true);
        setError(null);
        try {
            await onConfirm(serviceId, fte, allocationUnit, startDate, endDate);
            onClose();
        } catch {
            setError("Failed to save booking.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!onDelete) return;
        setDeleting(true);
        setError(null);
        try {
            await onDelete();
            onClose();
        } catch {
            setError("Failed to delete booking.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
        >
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{isEdit ? "Edit Booking" : "New Booking"}</DialogTitle>
                    <p className="text-base font-semibold text-slate-800">{state.individualName}</p>
                </DialogHeader>

                <div className="space-y-3 py-2 overflow-y-auto max-h-[calc(100vh-10rem)]">
                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Period</label>
                        <div className="flex items-center gap-2">
                            <Input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                className="w-36"
                            />
                            <span className="text-slate-400">—</span>
                            <Input
                                type="date"
                                value={endDate}
                                min={startDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                className="w-36"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                            Project / Budget / Service
                        </label>
                        <div className="border rounded-md divide-y">
                            <div className="flex items-center gap-2 px-3 py-1">
                                <span className="text-xs text-slate-400 w-14 shrink-0">Project</span>
                                <Select value={projectId} onValueChange={handleProjectChange}>
                                    <SelectTrigger className="border-0 shadow-none h-7 flex-1">
                                        <SelectValue placeholder="Select…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {projects.map((p) => (
                                            <SelectItem key={p.id} value={p.id}>
                                                {p.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {projectId && (
                                <div className="flex items-center gap-2 px-3 py-1">
                                    <span className="text-xs text-slate-400 w-14 shrink-0">Budget</span>
                                    <Select value={budgetId} onValueChange={handleBudgetChange}>
                                        <SelectTrigger className="border-0 shadow-none h-7 flex-1">
                                            <SelectValue placeholder="Select…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {budgets.map((b) => (
                                                <SelectItem key={b.id} value={b.id}>
                                                    {b.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            {budgetId && (
                                <div className="flex items-center gap-2 px-3 py-1">
                                    <span className="text-xs text-slate-400 w-14 shrink-0">Service</span>
                                    <Select value={serviceId} onValueChange={setServiceId}>
                                        <SelectTrigger className="border-0 shadow-none h-7 flex-1">
                                            <SelectValue placeholder="Select a department…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {services.map((c) => (
                                                <SelectItem key={c.id} value={c.id}>
                                                    {deptName(c.departmentId)} — {c.committedManDays.toFixed(1)} MD
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                        </div>
                    </div>

                    {committedMd !== null && bookedMd !== null && remainingMd !== null && (
                        <div className="rounded-md bg-slate-50 border px-3 py-2 text-xs space-y-1.5">
                            <div className="flex items-center gap-2">
                                <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                                    <div
                                        className={clsx(
                                            "h-2 rounded-full transition-all",
                                            remainingMd >= 0 ? "bg-blue-500" : "bg-red-500",
                                        )}
                                        style={{
                                            width: `${Math.min(100, committedMd > 0 ? (bookedMd / committedMd) * 100 : 100)}%`,
                                        }}
                                    />
                                </div>
                                <span
                                    className={clsx(
                                        "font-semibold whitespace-nowrap",
                                        remainingMd === 0
                                            ? "text-green-600"
                                            : remainingMd > 0
                                              ? "text-amber-600"
                                              : "text-red-600",
                                    )}
                                >
                                    {remainingMd >= 0
                                        ? `${remainingMd.toFixed(1)} MD remaining`
                                        : `${Math.abs(remainingMd).toFixed(1)} MD over`}
                                </span>
                            </div>
                            <div className="flex justify-between text-slate-400">
                                <span>{bookedMd.toFixed(1)} MD booked</span>
                                <span>{committedMd.toFixed(1)} MD committed</span>
                            </div>
                        </div>
                    )}

                    {capacityData !== undefined && (
                        <div className="flex flex-col gap-1">
                            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                                {state.individualName.split(" ")[0]}&apos;s Capacity
                            </label>
                            <div className="flex gap-4 text-sm text-slate-700">
                                <p>
                                    <span className="font-semibold">
                                        {capacityData.effectiveCapacityMd.toFixed(1)} MD
                                    </span>
                                    <span className="text-slate-400"> effective</span>
                                </p>
                                <p>
                                    <span
                                        className={clsx(
                                            "font-semibold",
                                            capacityData.effectiveCapacityMd - capacityData.bookedMd < 0
                                                ? "text-red-600"
                                                : "text-green-700",
                                        )}
                                    >
                                        {(capacityData.effectiveCapacityMd - capacityData.bookedMd).toFixed(1)} MD
                                    </span>
                                    <span className="text-slate-400"> available</span>
                                </p>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col gap-2">
                        <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Allocation</label>
                        <div className="flex gap-1 rounded-md border p-0.5 w-fit bg-slate-50">
                            <button
                                type="button"
                                onClick={() => setAllocationUnit("fte_pct")}
                                className={clsx(
                                    "text-xs px-3 py-1 rounded transition-colors",
                                    allocationUnit === "fte_pct"
                                        ? "bg-white shadow-sm text-slate-800 font-medium"
                                        : "text-slate-500 hover:text-slate-700",
                                )}
                            >
                                % of capacity
                            </button>
                            <button
                                type="button"
                                onClick={() => setAllocationUnit("fte_fraction")}
                                className={clsx(
                                    "text-xs px-3 py-1 rounded transition-colors",
                                    allocationUnit === "fte_fraction"
                                        ? "bg-white shadow-sm text-slate-800 font-medium"
                                        : "text-slate-500 hover:text-slate-700",
                                )}
                            >
                                Budget FTE
                            </button>
                        </div>

                        {allocationUnit === "fte_pct" ? (
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min={1}
                                    max={100}
                                    step={10}
                                    value={ftePct}
                                    onChange={(e) => setFtePct(e.target.value)}
                                    className="w-24"
                                />
                                <span className="text-sm text-slate-500">
                                    % of {state.individualName}&apos;s effective capacity in the selected period which
                                    is{" "}
                                    {newContributionMd !== null
                                        ? newContributionMd.toFixed(1)
                                        : (capacityData?.effectiveCapacityMd.toFixed(1) ?? "…")}{" "}
                                    MDs
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <Input
                                    type="number"
                                    min={0.1}
                                    max={1.0}
                                    step={0.1}
                                    value={fteFraction}
                                    onChange={(e) => setFteFraction(e.target.value)}
                                    className="w-24"
                                />
                                <span className="text-sm text-slate-500">
                                    fraction of the budget man-month
                                    {selectedBudget && !isNaN(fteFractionNum) && fteFractionNum > 0 && (
                                        <span className="ml-1 text-slate-400">
                                            = {(fteFractionNum * manDaysPerManMonth).toFixed(1)} days/month
                                            {startDate && endDate && endDate >= startDate && (
                                                <span>
                                                    {" "}
                                                    which is{" "}
                                                    {(
                                                        fteFractionNum *
                                                        manDaysPerManMonth *
                                                        countMonths(startDate, endDate)
                                                    ).toFixed(1)}{" "}
                                                    MD total for the period
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>

                    {dateOutsideBudget && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                            ⚠ The booking period extends outside the selected budget (
                            {selectedBudget!.startDate.slice(0, 7)} — {selectedBudget!.endDate.slice(0, 7)}).
                        </div>
                    )}

                    {wouldExceedCommitment && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                            ⚠ This booking would exceed the service commitment — total booked would reach{" "}
                            {(bookedMd! - previousContributionMd + newContributionMd!).toFixed(1)} MD against{" "}
                            {committedMd!.toFixed(1)} MD committed.
                        </div>
                    )}

                    {wouldOverbookIndividual && (
                        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                            ⚠ This booking would overbook {state.individualName} —{" "}
                            {(fteFractionNum * manDaysPerManMonth * countMonths(startDate, endDate)).toFixed(1)} MD
                            booked against {capacityData!.effectiveCapacityMd.toFixed(1)} MD capacity.
                        </div>
                    )}

                    {error && <p className="text-sm text-red-600">{error}</p>}
                </div>

                <DialogFooter className="flex items-center gap-2 sm:justify-between">
                    {isEdit && onDelete ? (
                        <Button variant="destructive" onClick={handleDelete} disabled={deleting || saving}>
                            {deleting ? "Deleting…" : "Delete"}
                        </Button>
                    ) : (
                        <span />
                    )}
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose} disabled={saving || deleting}>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirm} disabled={saving || deleting}>
                            {saving ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
